/**
 * Error classes for axios client token refresh failures.
 *
 * These discriminated error types allow callers to distinguish between:
 * - Genuine authentication failures (401/400/403 - should logout)
 * - Infrastructure failures (network errors, timeouts, cold starts - should retry)
 */

/**
 * Error thrown when token refresh fails due to authentication issues.
 *
 * This indicates a genuine auth problem (expired refresh token, invalid credentials, etc.)
 * and should typically result in logging the user out.
 *
 * Status codes that trigger this error:
 * - 400 Bad Request (malformed refresh token)
 * - 401 Unauthorized (refresh token expired/invalid)
 * - 403 Forbidden (refresh token lacks permission)
 *
 * @example
 * ```typescript
 * try {
 *   await refreshToken();
 * } catch (error) {
 *   if (error instanceof TokenRefreshAuthError) {
 *     // Logout user - their refresh token is no longer valid
 *     logout();
 *   }
 * }
 * ```
 */
export class TokenRefreshAuthError extends Error {
  public readonly isInfrastructureIssue = false;
  public readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'TokenRefreshAuthError';
    this.statusCode = statusCode;

    // Maintain proper stack trace for where error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, TokenRefreshAuthError);
    }
  }
}

/**
 * Error thrown when token refresh fails due to network/infrastructure issues.
 *
 * This indicates a temporary problem (backend cold start, network timeout, etc.)
 * and should typically allow retry without logging the user out.
 *
 * Conditions that trigger this error:
 * - ECONNREFUSED (backend not running/starting up)
 * - ECONNABORTED (connection aborted)
 * - ETIMEDOUT (request timeout)
 * - NETWORK_ERROR (generic network failure)
 * - 503 Service Unavailable
 * - 504 Gateway Timeout
 * - No response object (connection failed before HTTP layer)
 *
 * @example
 * ```typescript
 * try {
 *   await refreshToken();
 * } catch (error) {
 *   if (error instanceof TokenRefreshNetworkError) {
 *     // Show retry UI - backend might be cold starting
 *     showRetryDialog(error.message);
 *   }
 * }
 * ```
 */
export class TokenRefreshNetworkError extends Error {
  public readonly isInfrastructureIssue = true;
  public readonly originalError: unknown;
  public readonly uiAlreadyShown: boolean;
  public readonly isColdStart: boolean;

  constructor(message: string, originalError: unknown, uiAlreadyShown = false, isColdStart = false) {
    super(message);
    this.name = 'TokenRefreshNetworkError';
    this.originalError = originalError;
    this.uiAlreadyShown = uiAlreadyShown;
    this.isColdStart = isColdStart;

    // Maintain proper stack trace for where error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, TokenRefreshNetworkError);
    }
  }
}
